import React, { forwardRef, useMemo, useState, useCallback, useEffect } from 'react'

import PropTypes from 'prop-types'

import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import lowerCase from 'lodash/lowerCase'
import toNumber from 'lodash/toNumber'
import toString from 'lodash/toString'

import harvestCropManagementSchema from '@smartcoop/forms/schemas/management/harvestCropManagement.schema'
import { useT } from '@smartcoop/i18n'
import Form from '@smartcoop/web-components/Form'
import InputPercentage from '@smartcoop/web-components/InputPercentage'
import InputSelect from '@smartcoop/web-components/InputSelect'
import InputUnit from '@smartcoop/web-components/InputUnit'

const HarvestForm = forwardRef(({ defaultValues, handleSubmit, growingSeason, readOnly, crops, area, growingSeasonCosts, shouldDisplayCosts, concludeManagement }, formRef) => {
  const t = useT()
  const [unit, setUnit] = useState(defaultValues?.unitProductivity || '')
  const [projectedReceipt, setProjectedReceipt] = useState(null)

  const { crop, activity } = growingSeason

  const currentCrop = find(crops, item => item?.cropId === growingSeason?.cropId)

  const calculateProjectedReceipt = useCallback(() => {
    if (!shouldDisplayCosts) return
    const productivityKg = formRef?.current?.getFieldValue?.('productivityKg')
    const productivityBags = formRef?.current?.getFieldValue?.('productivityBags')
    const dayCotation = toNumber(formRef?.current?.getFieldValue?.('dayCotation'))
    const productivity = unit === 'kg/ha' ? toNumber(productivityKg)/toNumber(currentCrop?.extraAttributes?.convertionKgToBag || 1) : toNumber(productivityBags)

    if(isEmpty(toString(productivity)) || productivity === 0 || isEmpty(toString(unit)) || isEmpty(toString(dayCotation)) || dayCotation === 0) {
      setProjectedReceipt(null)
      return
    }
    setProjectedReceipt(toString(productivity * area * dayCotation))
  }, [area, currentCrop, formRef, shouldDisplayCosts, unit])


  const handleUnitsChange = useCallback(
    (selectedItem) => {
      setUnit(selectedItem.target.value)
      calculateProjectedReceipt()
    },
    [calculateProjectedReceipt]
  )

  const unitProductivity = useMemo(
    () => (
      [
        {
          label: 'kg/ha',
          value: 'kg/ha'
        },
        {
          label: 'sacas/ha',
          value: 'sacas/ha'
        }
      ]
    ),
    []
  )


  useEffect(() => {
    if(formRef.current && defaultValues?.unitProductivity) {
      calculateProjectedReceipt()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, formRef.current])

  const inputProductivity = useMemo(
    () => {
      const inputTypeProductivity = {
        'kg/ha':
          <InputUnit
            label={ t('productivity') }
            name="productivityKg"
            fullWidth
            unit="kg/ha"
            maxLength={ 9 }
            defaultValue={ defaultValues?.productivityKg || '' }
            disabled={ readOnly }
            onChange={ calculateProjectedReceipt }
            type="float"
            decimalScale={ 1 }
          />,
        'sacas/ha':
          <InputUnit
            label={ t('productivity') }
            name="productivityBags"
            fullWidth
            unit="sacas/ha"
            type="integer"
            maxLength={ 6 }
            defaultValue={ defaultValues?.productivityBags || '' }
            disabled={ readOnly }
            onChange={ calculateProjectedReceipt }
          />
      }
      return inputTypeProductivity[unit]

    },
    [calculateProjectedReceipt, defaultValues.productivityBags, defaultValues.productivityKg, readOnly, t, unit]
  )

  const complementaryForm = useMemo(
    () => {
      const cropSelectForm = {
        'trigo':
          <InputPercentage
            name="ph"
            label="ph"
            maxLength={ 2 }
            maxValue={ 99 }
            unit="%"
            type="integer"
            fullWidth
            defaultValue={ defaultValues?.ph  }
            disabled={ readOnly }
          />,
        'cevada':
          <>
            <InputPercentage
              name="ph"
              label="ph"
              maxLength={ 2 }
              maxValue={ 99 }
              type="integer"
              fullWidth
              defaultValue={ defaultValues?.ph }
              disabled={ readOnly }
            />
            <InputPercentage
              name="germinationProductivity"
              label={ t('germination') }
              type="integer"
              fullWidth
              defaultValue={ defaultValues?.germinationProductivity }
              disabled={ readOnly }
            />
          </>,
        'aveia':
          <InputPercentage
            name="ph"
            label="ph"
            maxLength={ 2 }
            maxValue={ 99 }
            type="integer"
            fullWidth
            defaultValue={ defaultValues?.ph }
            disabled={ readOnly }
          />,
        'arroz':
          <>
            <InputPercentage
              name="wholeGrain"
              label={ t('whole grain') }
              type="integer"
              fullWidth
              defaultValue={ defaultValues?.wholeGrain }
              disabled={ readOnly }
            />
            <InputPercentage
              name="brokenGrain"
              label={ t('broken grain') }
              type="integer"
              fullWidth
              defaultValue={ defaultValues?.brokenGrain }
              disabled={ readOnly }
            />
            <InputPercentage
              name="shellAndBran"
              label={ t('shell and bran') }
              type="integer"
              fullWidth
              defaultValue={ defaultValues?.shellAndBran }
              disabled={ readOnly }
            />
          </>
      }

      return cropSelectForm[lowerCase(crop?.description)]
    },
    [crop.description, defaultValues.brokenGrain, defaultValues.germinationProductivity, defaultValues.ph, defaultValues.shellAndBran, defaultValues.wholeGrain, readOnly, t]
  )

  return (
    <Form
      ref={ formRef }
      schemaConstructor={ harvestCropManagementSchema }
      containerStyle={ { height: 'auto' } }
      onSubmit={ handleSubmit }
      schemaProps={ { concludeManagement, extraAttributes: currentCrop?.extraAttributes } }
    >
      <InputSelect
        label={ t('inform produtivity') }
        name="unitProductivity"
        options={ unitProductivity }
        onChange={ handleUnitsChange }
        defaultValue={ defaultValues?.unitProductivity }
        disabled={ readOnly }
      />
      { unit && (
        inputProductivity
      )}

      { unit && shouldDisplayCosts && (
        <InputUnit
          name="dayCotation"
          label={ t('day cotation') }
          unit={ t('price/bag') }
          decimalScale={ 2 }
          type="float"
          fullWidth
          onChange={ calculateProjectedReceipt }
          defaultValue={ defaultValues?.dayCotation }
          disabled={ readOnly }
        />
      )}

      { unit && shouldDisplayCosts && (
        <InputUnit
          key={ `receipt-${ projectedReceipt }` }
          detached
          label={ t('projected receipt') }
          value={ toNumber(projectedReceipt) }
          unit={ t('R$') }
          decimalScale={ 2 }
          type="float"
          fullWidth
          disabled
        />
      )}

      { unit && shouldDisplayCosts && (
        <InputUnit
          detached
          label={ t('expected result') }
          value={ toNumber(projectedReceipt || '0')  + toNumber(growingSeasonCosts?.productionCost || '0')  }
          unit={ t('R$') }
          decimalScale={ 2 }
          type="float"
          fullWidth
          disabled
        />
      )}

      { unit && shouldDisplayCosts && (
        <InputUnit
          detached
          label={ t('expected result by ha') }
          value={ (toNumber(projectedReceipt || '0')  + toNumber(growingSeasonCosts?.productionCost || '0')) / area  }
          unit={ t('R$/ha') }
          decimalScale={ 2 }
          type="float"
          fullWidth
          disabled
        />
      )}

      {activity?.slug === 'graos' && (
        <InputPercentage
          name="impurity"
          label={ t('impurity percentage') }
          isAllowed={ (values) => {
            const { floatValue } = values
            return floatValue === undefined || floatValue <= 99.9
          } }
          decimalScale={ 1 }
          type="float"
          fullWidth
          defaultValue={ defaultValues?.impurity }
          disabled={ readOnly }
        />
      )}

      {activity?.slug === 'graos' && (
        <InputPercentage
          name="humidity"
          label={ t('humidity percentage') }
          isAllowed={ (values) => {
            const { floatValue } = values
            return floatValue === undefined || floatValue <= 99.9
          } }
          decimalScale={ 1 }
          type="float"
          fullWidth
          defaultValue={ defaultValues?.humidity }
          disabled={ readOnly }
        />
      )}

      { crop?.description && (
        complementaryForm
      )}

    </Form>
  )
})

HarvestForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  growingSeason: PropTypes.object.isRequired,
  growingSeasonCosts: PropTypes.object,
  crops: PropTypes.array,
  readOnly: PropTypes.bool,
  area: PropTypes.number.isRequired,
  shouldDisplayCosts: PropTypes.bool,
  concludeManagement: PropTypes.bool,
  defaultValues: PropTypes.object
}

HarvestForm.defaultProps = {
  readOnly: false,
  shouldDisplayCosts: false,
  concludeManagement: false,
  growingSeasonCosts: {},
  crops: [],
  defaultValues: {}
}

export default HarvestForm
